import { useCallback, useEffect, useMemo, useState } from "react";
import { useGiveStudyConsentMutation } from "render/hooks/mutations/consent/useGiveStudyConsentMutation";
import { useAsyncHandle } from "render/hooks/useAsyncHandle";
import { LegalDocumentLayout } from "render/ui/layout/LegalDocumentLayout/LegalDocumentLayout";
import { Toggle } from "render/ui/presentation/Toggle";
import { ActionButton } from "render/ui/trigger/ActionButton";
import { useConsentsMap } from "render/views/ProfileView/components/DataAndPrivacySection/hooks/useConsentsMap";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

interface ConsentRequiredStudiesDocumentProps {
  notParticipatedStudies: string[];
  onDone: () => Promise<any>;
  onRefreshConsents: () => Promise<any>;
}

function getInitialState(
  { studies }: ReturnType<typeof useConsentsMap>,
  notParticipatedStudies: string[]
) {
  return {
    [studies.DERMAFLOW]: !notParticipatedStudies.includes(studies.DERMAFLOW),
    [studies.SPECTRUM]: !notParticipatedStudies.includes(studies.SPECTRUM),
    [studies.CARDIO]: !notParticipatedStudies.includes(studies.CARDIO),
  };
}

export function ConsentRequiredStudiesDocument({
  onDone,
  notParticipatedStudies,
}: ConsentRequiredStudiesDocumentProps) {
  const giveStudyConsentMutation = useGiveStudyConsentMutation();
  const consentMap = useConsentsMap();

  const [toggleState, setToggleState] = useState(
    getInitialState(consentMap, notParticipatedStudies)
  );

  const submitHandler = useCallback(async () => {
    await Promise.all(
      notParticipatedStudies.map((study) =>
        giveStudyConsentMutation({
          documentUri: study,
          consentSignature: {
            $type: "login",
          },
        })
      )
    );

    return onDone();
  }, [notParticipatedStudies, giveStudyConsentMutation, onDone]);

  const handleSubmit = useAsyncHandle(submitHandler);

  useEffect(() => {
    setToggleState(getInitialState(consentMap, notParticipatedStudies));
  }, [notParticipatedStudies, consentMap]);

  const hasAgreedToAllStudies = useMemo(() => {
    return Object.values(toggleState).every((value) => value);
  }, [toggleState]);

  const toggleHandler = useCallback(async (study: string, value: boolean) => {
    setToggleState((prev) => ({
      ...prev,
      [study]: value,
    }));
  }, []);

  const handleToggle = useAsyncHandle(toggleHandler);

  return (
    <LegalDocumentLayout
      stickyActions
      Description={
        <div className={styles.header}>
          <h1>
            <Trans.Title />
          </h1>
          <p>
            <Trans.Summary />
          </p>
        </div>
      }
      Content={
        <div className={styles.body}>
          <h1>
            <Trans.HowItWorks />
          </h1>
          <Trans.StudyExplanation />
          <h2>
            <div className={styles.numerator}>
              <div>01</div>
              <div className={styles.line}></div>
            </div>
            <div className={styles.titleContainer}>
              <Toggle
                name="CARDIO-consent"
                label=""
                checked={toggleState[consentMap.studies.CARDIO]}
                onClick={(value) => {
                  handleToggle.callback(consentMap.studies.CARDIO, value);
                }}
              />
              <Trans.Study.Cardio.Head />
            </div>
          </h2>
          <Trans.Study.Cardio.Description />
          <h2>
            <div className={styles.numerator}>
              <div>02</div>
              <div className={styles.line}></div>
            </div>
            <div className={styles.titleContainer}>
              <Toggle
                name="SPECTRUM-consent"
                label=""
                checked={toggleState[consentMap.studies.SPECTRUM]}
                onClick={(value) => {
                  handleToggle.callback(consentMap.studies.SPECTRUM, value);
                }}
              />
              <Trans.Study.Spectrum.Head />
            </div>
          </h2>
          <Trans.Study.Spectrum.Description />

          <h2>
            <div className={styles.numerator}>
              <div>03</div>
              <div className={styles.line}></div>
            </div>
            <div className={styles.titleContainer}>
              <Toggle
                name="DERMAFLOW-consent"
                label=""
                checked={toggleState[consentMap.studies.DERMAFLOW]}
                onClick={(value) => {
                  handleToggle.callback(consentMap.studies.DERMAFLOW, value);
                }}
              />
              <Trans.Study.Derma.Head />
            </div>
          </h2>
          <Trans.Study.Derma.Description />
        </div>
      }
      Actions={
        <>
          <ActionButton
            onClick={handleSubmit.callback}
            disabled={!hasAgreedToAllStudies}
          >
            Agree and sign
          </ActionButton>
        </>
      }
    />
  );
}
